import { Sprite } from 'pixi.js';
import { DESIGN_HEIGHT, DESIGN_WIDTH } from '../../data/constants';
import type { ArenaBundle } from '../types';
import { dropAndExplodeBall, resetEmbeddedBalls, spawnEmbeddedBall } from './EmbeddedBall';
import { CANNONBALL_OBJECT_ID, THE_PIT_INTERACTIONS } from './interactions';
import { Torch } from './Torch';

// Per-torch placement + size. Tweak each torch independently here. `facing: -1`
// flips the flame so it leans away from the wall on the right side. `height` is
// in design-px (logical viewport coords); halo radius scales with it.
const TORCHES = [
  { x: 300, y: 85,  height: 80,  facing: 1 as const },
  { x: 544, y: 85,  height: 80,  facing: -1 as const },
  { x: 30,  y: 105, height: 120, facing: 1 as const },
  { x: 818, y: 105, height: 120, facing: -1 as const },
];

const ROOT = '/assets/textures/arenas/ThePit';

export const THE_PIT: ArenaBundle = {
  id: 'the_pit',
  name: 'The Pit',
  assets: {
    textures: {
      arenaTop: `${ROOT}/arena-top.png`,
      arenaBottom: `${ROOT}/arena-bottom.png`,
      // Reused from the cannon skill bundle. Pixi Assets caches by URL so the
      // texture is shared - no double-load cost.
      cannonball: '/assets/textures/characters/sparky_bot/skills/cannon/cannonball.png',
      // "Stuck in wall" pose - the embed sprite swaps to this once the fly-in
      // animation lands. Drawn with cracks / debris so it reads as lodged.
      cannonballInWall: `${ROOT}/cannonball-in-wall.png`,
      // Permanent damage mark left behind when the ball drops out (earthshake).
      // Replaces the lodged ball in the wall and persists for the rest of the
      // battle - cracks/hole where the ball was.
      damagedWall: `${ROOT}/damaged-wall.png`,
    },
    sheets: {
      torch: { url: `${ROOT}/torch-36.png`, cols: 6, rows: 6 },
    },
  },
  mount(ctx) {
    // new battle scene -> drop leftover embed refs from the previous battle
    resetEmbeddedBalls();
    // Background composed of two same-sized layers; arena-bottom paints OVER
    // arena-top (foreground railing/floor on top of the back wall).
    const top = new Sprite(ctx.textures.arenaTop);
    top.width = DESIGN_WIDTH;
    top.height = DESIGN_HEIGHT;
    ctx.layer.addChild(top);

    // Torches mounted on the back wall - between the two bg layers so the
    // foreground (arena-bottom) can occlude their lower portion if needed.
    const torchFrames = ctx.sheets.torch;
    if (torchFrames) {
      for (const cfg of TORCHES) {
        const torch = new Torch({
          frames: torchFrames,
          facing: cfg.facing,
          height: cfg.height,
        });
        torch.position.set(cfg.x, cfg.y);
        ctx.layer.addChild(torch);
      }
    }

    const bottom = new Sprite(ctx.textures.arenaBottom);
    bottom.width = DESIGN_WIDTH;
    bottom.height = DESIGN_HEIGHT;
    ctx.layer.addChild(bottom);
  },
  interactions: THE_PIT_INTERACTIONS,
  // Programmatic placeholder visuals (real sprites land later):
  //  attach  -> spawn the cannonball texture stuck in the upper wall above the
  //             blocking side, lightly tilted.
  //  release -> drop ball under the defender's feet, then play an additive
  //             expand-fade disc as a stand-in for the explosion.
  async playArenaEvent(event, ctx) {
    if (event.objectId !== CANNONBALL_OBJECT_ID) return;
    if (event.type === 'arena_attach') {
      const flightTex = ctx.textures.cannonball;
      if (!flightTex) return;
      // fromPosition is set when a skill (e.g. cannon on block) calls this
      // directly at impact - the embed flies in from there. Engine-emitted
      // arena_attach has no fromPosition; spawnEmbeddedBall is idempotent so
      // the second call after the skill no-ops cleanly.
      await spawnEmbeddedBall(event.side, flightTex, ctx, {
        fromPosition: event.fromPosition,
        lodgedTexture: ctx.textures.cannonballInWall,
      });
      return;
    }
    if (event.type === 'arena_release') {
      await dropAndExplodeBall(event.side, ctx);
    }
  },
};
